import { createContext, ReactNode, useState, useCallback } from 'react'

/**
 * ミュートコンテンツの表示履歴を管理するContext
 *
 * セッション中に一度「表示する」ボタンを押したミュートコンテンツは、
 * 再度オーバーレイを表示せずに直接表示する
 * - 写真: photo_url 単位で管理
 * - アルバム: share_id 単位で管理
 */

type MutedContentMemoryType = {
  // 写真用（既存）
  shouldShowContent: (photo_url: string, is_muted: boolean) => boolean
  markAsShown: (photo_url: string) => void

  // アルバム用（新規）
  shouldShowAlbum: (share_id: string, is_muted_owner: boolean) => boolean
  markAlbumAsShown: (share_id: string) => void

  // 共通
  clearAll: () => void
}

export const MutedContentMemoryContext = createContext<MutedContentMemoryType>(undefined as never)

export const MutedContentMemoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 表示済みの写真URLをSetで管理（重複排除のため）
  const [shown_photos, setShownPhotos] = useState<Set<string>>(new Set())
  // 表示済みの共有アルバムIDをSetで管理（重複排除のため）
  const [shown_albums, setShownAlbums] = useState<Set<string>>(new Set())

  // ミュートユーザーの写真を表示すべきかどうかを判定
  const shouldShowContent = useCallback((photo_url: string, is_muted: boolean) => {
    // ミュートされていない場合は常に表示
    if (!is_muted) {
      return true
    }

    // 表示履歴がある場合は表示
    if (shown_photos.has(photo_url)) {
      return true
    }

    // それ以外はオーバーレイを表示
    return false
  }, [shown_photos])

  const markAsShown = useCallback((photo_url: string) => {
    setShownPhotos(prev => new Set([...prev, photo_url]))
  }, [])

  // ミュートオーナーのアルバムを表示すべきかどうかを判定
  const shouldShowAlbum = useCallback((share_id: string, is_muted_owner: boolean) => {
    // ミュートオーナーでない場合は常に表示
    if (!is_muted_owner) {
      return true
    }

    // 表示履歴がある場合は表示
    if (shown_albums.has(share_id)) {
      return true
    }

    // それ以外はオーバーレイを表示
    return false
  }, [shown_albums])

  const markAlbumAsShown = useCallback((share_id: string) => {
    setShownAlbums(prev => new Set([...prev, share_id]))
  }, [])

  // ログアウト時などに使用
  const clearAll = useCallback(() => {
    setShownPhotos(new Set())
    setShownAlbums(new Set())
  }, [])

  return (
    <MutedContentMemoryContext value={{
      shouldShowContent,
      markAsShown,
      shouldShowAlbum,
      markAlbumAsShown,
      clearAll
    }}>
      {children}
    </MutedContentMemoryContext>
  )
}
